import { EgpSeason } from '../egp.service';

import {
  Component,
  effect,
  inject,
  Injector,
  input,
  untracked,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import * as d3Axis from 'd3-axis';
import * as d3Scale from 'd3-scale';
import * as d3 from 'd3-selection';
import { Observable } from 'rxjs';
import { forkJoin } from 'rxjs';

export interface RiskBoxPosition {
  x: number;
  y: number;
}

@Component({
  selector: 'app-riskbox',
  template: ``,
})
export class RiskboxComponent {
  private injector = inject(Injector);
  public translate = inject(TranslateService);

  riskBoxdata = input<any>();
  svgid = input<string>();
  mini = input<boolean>();
  season = input<EgpSeason>();

  translations = {
    REIF: null,
    NAESSE: null,
    NAESSE_SHORT: null,
    SCHNEE: null,
    EIS: null,
    THUNDERSTORM: null,
    STORM: null,
    HEAT: null,
    DRAIN: null,
    AXIS_AREA: null,
    AXIS_RISK: null,
    AREA: null,
    RISK: null,
    F0: null,
    FEX: null,
    F1: null,
    F2: null,
    F3: null,
    Gx: null,
    G1: null,
    G2: null,
    G3: null,
    WARNLEVEL: null,
  };
  private size: number;
  private width: number;
  private height: number;
  private margin: number; // = {top: 20, right: 20, bottom: 20, left: 20};
  private boxSize: number;

  private typeCfg = {
    freezingRain: {
      winter: { color: '#e14b7e', top: 1, left: -1 },
      summer: { color: '#ffb74d', top: -1, left: 1 },
      //      top: 1,
      //      left: -1
    },
    freezingWet: {
      winter: { color: '#373f43', top: -1, left: -1 },
      summer: { color: '#64b5f6', top: 1, left: 1 },
      //      top: -1,
      //      left: -1
    },
    rime: {
      winter: { color: '#9e9e9e', top: -1, left: 1 },
      summer: { color: '#e14b7e', top: 1, left: -1 },
      //      top: -1,
      //      left: 1
    },
    snow: {
      winter: { color: '#f9715f', top: 1, left: 1 },
      summer: { color: '#9e9e9e', top: -1, left: -1 },
      //      top: 1,
      //      left: 1
    },
  };

  private x: any;
  private y: any;
  private svg: any;
  private g: any;

  constructor() {
    this.listenToSeasonChanges();
    this.listenToRiskBoxDataChanges();
  }

  initTranslations(): void {
    this.translations = {
      REIF: null,
      NAESSE: null,
      NAESSE_SHORT: null,
      SCHNEE: null,
      EIS: null,
      THUNDERSTORM: null,
      STORM: null,
      HEAT: null,
      DRAIN: null,
      AXIS_AREA: null,
      AXIS_RISK: null,
      AREA: null,
      RISK: null,
      F0: null,
      FEX: null,
      F1: null,
      F2: null,
      F3: null,
      Gx: null,
      G1: null,
      G2: null,
      G3: null,
      WARNLEVEL: null,
    };
  }

  private async getTranslations(): Promise<void> {
    return new Promise(resolve => {
      const keyPrefix =
        this.season() === EgpSeason.WINTER
          ? 'PRODUCT.EGP.RISKBOX.'
          : 'PRODUCT.EGP-SUMMER.RISKBOX.';
      const observables: Observable<any>[] = [];
      const translationKeys = [];
      for (const translationKey of Object.keys(this.translations)) {
        if (!this.translations[translationKey]) {
          translationKeys.push(translationKey);
          observables.push(this.translate.get(keyPrefix + translationKey));
        }
      }
      forkJoin(observables).subscribe(dataArray => {
        for (let i = 0; i < dataArray.length; i++) {
          const translationKey = translationKeys[i];
          this.translations[translationKey] = dataArray[i];
        }
        resolve();
      });
    });
  }

  calculateRiskPoints(risk: number, side: string): number {
    if (side === 'left') {
      return this.boxSize * (1 - risk) + this.margin;
    } else {
      return this.boxSize * (1 + risk) + this.margin;
    }
  }

  calculateAreaPoints(area: number, side: string): number {
    if (side === 'top') {
      return this.boxSize + area * this.boxSize + this.margin;
    } else {
      return this.boxSize - area * this.boxSize + this.margin;
    }
  }

  calculateLevelPoints(level: number, side: string): number {
    if (side === 'left') {
      return this.boxSize * (1 - level) + this.margin;
    } else if (side === 'right') {
      return this.boxSize * (1 + level) + this.margin;
    } else if (side === 'top') {
      return this.boxSize + level * this.boxSize + this.margin;
    } else {
      return this.boxSize - level * this.boxSize + this.margin;
    }
  }

  private async createRiskBox(): Promise<void> {
    this.initSvg();
    this.initAxis();
    const season = this.season();
    if (season === EgpSeason.WINTER) {
      this.drawGridLinesWinter();
      this.drawBoxesWinter();
    } else {
      this.drawGridLinesSummer();
      this.drawBoxesSummer();
    }
    this.drawAxis();
    this.drawCross();
    if (this.width > 180) {
      this.drawAxisTitles();
      this.drawTypes();
      if (season === EgpSeason.WINTER) {
        this.drawGridTitlesWinter();
      } else {
        this.drawGridTitlesSummer();
      }
    }
  }

  private initSvg(): void {
    this.size = this.mini() ? 150 : 300;
    const svgid = this.svgid();
    d3.select('div#' + svgid);
    d3.select('div#' + svgid)
      .select('svg')
      .remove();
    this.svg = d3
      .select('div#' + svgid)
      .append('svg:svg')
      .attr('width', this.size)
      .attr('height', this.size);
    this.margin = this.size / 25;
    this.width = this.size - 4 * this.margin;
    this.height = this.size - 4 * this.margin;

    this.boxSize = (this.width - this.margin - this.margin) / 2;

    this.g = this.svg
      .append('g')
      .attr(
        'transform',
        'translate(' + this.margin * 2 + ',' + this.margin * 2 + ')'
      );
  }

  private drawBoxesWinter(): void {
    const div = d3
      .select('#' + this.svgid())
      .append('div')
      .attr('class', 'rbtooltip')
      .style('position', 'absolute')
      .style('text-align', 'left')
      .style('font-size', 'normal')
      .style('padding', '2px')
      .style('background', 'lightsteelblue')
      .style('border', '0px')
      .style('border-radius', '8px')
      .style('pointer-events', 'none')
      .style('opacity', 0.0);

    const riskBoxdata = this.riskBoxdata();
    for (const type in riskBoxdata) {
      const title = this.translate.instant('PRODUCT.EGP.RISKBOX.' + type);
      const area = riskBoxdata[type].regionPercent / 100;
      const risk = riskBoxdata[type].riskPercent / 100;
      const pos = this.getBoxPosition(type, area, risk);
      this.g
        .append('rect')
        .attr('class', 'bar')
        .attr('width', this.boxSize * area)
        .attr('height', this.boxSize * risk)
        .attr('x', pos.x)
        .attr('y', pos.y)
        .attr('fill', this.typeCfg[type][this.season()].color)
        .on('mouseover', d => {
          div.style('opacity', 0.9);
          div
            .html(
              '<strong>' +
                title +
                '</strong>' +
                // + '<br/>' + this.translations.AXIS_AREA + ': ' + this.areaMapping(area)
                '<br/>' +
                this.areaMapping(area) +
                '<br/>' +
                this.translations.AXIS_RISK +
                ': ' +
                this.riskMapping(risk) +
                '%'
            )
            .style('left', pos.x + (this.boxSize * area) / 2 + 'px')
            .style('top', pos.y + this.boxSize * risk + 'px');
        })
        .on('mouseout', function (d) {
          div.style('opacity', 0);
        });
    }
  }

  private drawBoxesSummer(): void {
    const div = d3
      .select('#' + this.svgid())
      .append('div')
      .attr('class', 'rbtooltip')
      .style('position', 'absolute')
      .style('text-align', 'left')
      .style('font-size', 'large')
      .style('padding', '2px')
      .style('background', 'lightsteelblue')
      .style('border', '0px')
      .style('border-radius', '8px')
      .style('pointer-events', 'none');

    const riskBoxdata = this.riskBoxdata();
    for (const type in riskBoxdata) {
      const title = this.translate.instant(
        'PRODUCT.EGP-SUMMER.RISKBOX.' + type
      );
      const warn = this.translate.instant('PRODUCT.EGP.warn');

      const area = this.mappingArea(riskBoxdata[type].regionPercent);
      const risk = this.mappingArea(riskBoxdata[type].regionPercent);
      const pos = this.getBoxPosition(type, area, risk);
      this.g
        .append('rect')
        .attr('class', 'bar')
        .attr('width', this.boxSize * area)
        .attr('height', this.boxSize * risk)
        .attr('x', pos.x)
        .attr('y', pos.y)
        .attr('fill', this.typeCfg[type][this.season()].color)
        .on('mouseover', d => {
          div.style('opacity', 0.9);
          div
            .html(
              '<strong>' +
                title +
                '</strong>' +
                '<br/> ' +
                warn +
                ': ' +
                area * 4
            )
            .style('left', pos.x + (this.boxSize * area) / 2 + 'px')
            .style('top', pos.y + this.boxSize * risk + 'px');
        })
        .on('mouseout', function (d) {
          div.style('opacity', 0);
        });
    }
  }

  private areaMapping(area: number): string {
    const mapping = {
      0: this.translations.F0,
      0.15: this.translations.FEX,
      0.4: this.translations.F1,
      0.7: this.translations.F2,
      1: this.translations.F3,
    };
    return mapping[area];
  }

  mappingArea(metmanValue: number): number {
    // 0 10 30 70 100
    const mapping = {
      0: 0,
      10: 0.25,
      30: 0.5,
      70: 0.75,
      100: 1,
    };
    return mapping[metmanValue];
  }

  private riskMapping(risk: number): number {
    return risk * 100;
  }

  private getBoxPosition(
    type: string,
    area: number,
    risk: number
  ): RiskBoxPosition {
    const cfg = this.typeCfg[type][this.season()];
    const margin = this.margin;
    const xPos =
      cfg.left > 0
        ? this.boxSize + margin
        : this.boxSize - area * this.boxSize + margin;
    const yPos =
      cfg.top > 0 ? this.boxSize * (1 - risk) + margin : this.boxSize + margin;
    const position: RiskBoxPosition = { x: xPos, y: yPos };
    return position;
  }

  private drawTypes(): void {
    let fontsize: string;
    if (this.width > 400) {
      fontsize = 'large';
    } else if (this.width > 250) {
      fontsize = 'medium';
    } else {
      fontsize = 'small';
    }

    const season = this.season();
    if (season === EgpSeason.WINTER) {
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('x', this.boxSize / 4)
        .attr('y', -10)
        .attr('fill', this.typeCfg.freezingRain[season].color)
        .text(this.translations.EIS);

      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('x', this.boxSize / 4 + 12)
        .attr('y', this.height + 20)
        .attr('fill', this.typeCfg.freezingWet[season].color)
        .text(this.translations.NAESSE_SHORT);

      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('x', this.boxSize * 2.05)
        .attr('y', this.height + 20)
        .attr('fill', this.typeCfg.rime[season].color)
        .text(this.translations.REIF);

      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('x', this.boxSize * 2)
        .attr('y', -10)
        .attr('fill', this.typeCfg.snow[season].color)
        .text(this.translations.SCHNEE);
    } else {
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('x', this.boxSize / 4)
        .attr('y', -10)
        .attr('fill', this.typeCfg.rime[season].color)
        .text(this.translations.THUNDERSTORM);

      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('x', this.boxSize / 4 + 12)
        .attr('y', this.height + 20)
        .attr('fill', this.typeCfg.snow[season].color)
        .text(this.translations.STORM);

      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('x', this.boxSize * 2)
        .attr('y', -10)
        .attr('fill', this.typeCfg.freezingWet[season].color)
        .text(this.translations.DRAIN);

      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('x', this.boxSize * 2.05)
        .attr('y', this.height + 20)
        .attr('fill', this.typeCfg.freezingRain[season].color)
        .text(this.translations.HEAT);
    }
  }

  private drawAxisTitles(): void {
    let fontsize: string;
    if (this.width > 400) {
      fontsize = 'medium';
    } else if (this.width > 250) {
      fontsize = 'small';
    } else {
      fontsize = 'x-small';
    }

    const season = this.season();
    const xText =
      season === EgpSeason.WINTER
        ? this.translations.AREA
        : this.translations.WARNLEVEL;
    const yText =
      season === EgpSeason.WINTER
        ? this.translations.RISK + ' [%]'
        : this.translations.WARNLEVEL;

    this.g
      .append('text')
      .attr('font-size', fontsize)
      .attr('text-anchor', 'middle')
      .attr('fill', '#4B4B4B')
      .attr('x', this.width / 2)
      .attr('y', -10)
      .text(yText);
    this.g
      .append('text')
      .attr('font-size', fontsize)
      .attr('text-anchor', 'middle')
      .attr('fill', '#4B4B4B')
      .attr('x', this.width / 2)
      .attr('y', this.height + 20)
      .text(yText);
    this.g
      .append('text')
      .attr('font-size', fontsize)
      .attr('text-anchor', 'middle')
      .attr('fill', '#4B4B4B')
      .attr('x', -12)
      .attr('y', this.height / 2)
      .text(xText)
      .attr('transform', 'rotate(270, -12, ' + this.height / 2 + ')');
    this.g
      .append('text')
      .attr('font-size', fontsize)
      .attr('text-anchor', 'middle')
      .attr('fill', '#4B4B4B')
      .attr('x', this.width + 12)
      .attr('y', this.height / 2)
      .text(xText)
      .attr(
        'transform',
        'rotate(90, ' + (this.width + 10) + ', ' + this.height / 2 + ')'
      );
  }

  private drawGridTitlesWinter(): void {
    let fontsize: string;
    if (this.width > 400) {
      fontsize = 'medium';
    } else if (this.width > 250) {
      fontsize = 'small';
    } else {
      fontsize = 'x-small';
    }

    const areaTranslation = {
      0.15: 'Gx',
      0.4: 'G1',
      0.7: 'G2',
      1: 'G3',
    };
    const riskTranslation = {
      0.25: '25',
      0.5: '50',
      0.75: '75',
      0.1: '',
    };

    for (const area of [0.15, 0.4, 0.7, 1]) {
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.calculateAreaPoints(area, 'top'))
        .attr('y', this.height)
        .text(this.translations[areaTranslation[area]]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.calculateAreaPoints(area, 'bottom'))
        .attr('y', this.height)
        .text(this.translations[areaTranslation[area]]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.calculateAreaPoints(area, 'top'))
        .attr('y', 10)
        .text(this.translations[areaTranslation[area]]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.calculateAreaPoints(area, 'bottom'))
        .attr('y', 10)
        .text(this.translations[areaTranslation[area]]);
    }
    for (const risk of [0.25, 0.5, 0.75, 0.1]) {
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', 0)
        .attr('y', this.calculateRiskPoints(risk, 'left') + 4)
        .text(riskTranslation[risk]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', 0)
        .attr('y', this.calculateRiskPoints(risk, 'right') + 4)
        .text(riskTranslation[risk]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.width)
        .attr('y', this.calculateRiskPoints(risk, 'left') + 4)
        .text(riskTranslation[risk]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.width)
        .attr('y', this.calculateRiskPoints(risk, 'right') + 4)
        .text(riskTranslation[risk]);
    }
  }

  private drawGridTitlesSummer(): void {
    let fontsize: string;
    if (this.width > 400) {
      fontsize = 'medium';
    } else if (this.width > 250) {
      fontsize = 'small';
    } else {
      fontsize = 'x-small';
    }
    const summerTranslation = {
      0.25: '1',
      0.5: '2',
      0.75: '3',
      1.0: '4',
    };

    for (const level of [0.25, 0.5, 0.75, 1.0]) {
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.calculateLevelPoints(level, 'top'))
        .attr('y', this.height)
        .text(summerTranslation[level]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.calculateLevelPoints(level, 'bottom'))
        .attr('y', this.height)
        .text(summerTranslation[level]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.calculateLevelPoints(level, 'top'))
        .attr('y', 10)
        .text(summerTranslation[level]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.calculateLevelPoints(level, 'bottom'))
        .attr('y', 10)
        .text(summerTranslation[level]);
    }

    for (const level of [0.25, 0.5, 0.75, 1.0]) {
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', 0)
        .attr('y', this.calculateLevelPoints(level, 'left') + 4)
        .text(summerTranslation[level]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', 0)
        .attr('y', this.calculateLevelPoints(level, 'right') + 4)
        .text(summerTranslation[level]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.width)
        .attr('y', this.calculateLevelPoints(level, 'left') + 4)
        .text(summerTranslation[level]);
      this.g
        .append('text')
        .attr('font-size', fontsize)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9e9e9e')
        .attr('x', this.width)
        .attr('y', this.calculateLevelPoints(level, 'right') + 4)
        .text(summerTranslation[level]);
    }
  }

  private drawCross(): void {
    this.initArrow();

    const borderOffset = this.mini() ? 0 : 10;
    const lineX = this.g
      .append('line')
      .attr('x1', borderOffset / 2)
      .attr('x2', this.width - borderOffset)
      .attr('y1', this.height / 2)
      .attr('y2', this.height / 2)
      .attr('stroke', '#4B4B4B');
    if (this.width > 200) {
      lineX
        .attr('marker-end', 'url(#arrowend)')
        .attr('marker-start', 'url(#arrowleft)');
    }
    const lineY = this.g
      .append('line')
      .attr('x1', this.height / 2)
      .attr('x2', this.height / 2)
      .attr('y1', borderOffset / 2)
      .attr('y2', this.width - borderOffset)
      .attr('stroke', '#4B4B4B');
    if (this.width > 200) {
      lineY
        .attr('marker-end', 'url(#arrowend)')
        .attr('marker-start', 'url(#arrowtop)');
    }
  }

  private initArrow(): void {
    let markersize: number;
    let refY: number;
    if (this.width > 400) {
      markersize = 15;
      refY = 5;
    } else if (this.width > 200) {
      markersize = 10;
      refY = 5;
    } else {
      markersize = 5;
      refY = 2;
    }
    this.svg
      .append('marker')
      .attr('id', 'arrowleft')
      .attr('markerWidth', markersize)
      .attr('markerHeight', markersize)
      .attr('orient', '180')
      .attr('markerUnits', 'strokeWidth')
      .attr('refY', refY)
      .attr('refX', '0')
      .append('path')
      .attr('d', 'M 0,0 l 10,5 l -10,5 z');
    this.svg
      .append('marker')
      .attr('id', 'arrowend')
      .attr('markerWidth', markersize)
      .attr('markerHeight', markersize)
      .attr('orient', 'auto')
      .attr('markerUnits', 'strokeWidth')
      .attr('refY', refY)
      .attr('refX', '0')
      .append('path')
      .attr('d', 'M 0,0 l 10,5 l -10,5 z');
    this.svg
      .append('marker')
      .attr('id', 'arrowtop')
      .attr('markerWidth', markersize)
      .attr('markerHeight', markersize)
      .attr('orient', '270')
      .attr('markerUnits', 'strokeWidth')
      .attr('refY', refY)
      .attr('refX', '0')
      .append('path')
      .attr('d', 'M 0,0 l 10,5 l -10,5 z');
  }

  private drawGridLinesWinter(): void {
    // add the X gridlines
    const borderOffset = this.mini() ? 0 : 10;
    for (const risk of [0.25, 0.5, 0.75, 0.99]) {
      this.g
        .append('line')
        .attr('y1', this.calculateRiskPoints(risk, 'left'))
        .attr('y2', this.calculateRiskPoints(risk, 'left'))
        .attr('x1', borderOffset)
        .attr('x2', this.width - borderOffset)
        .attr('stroke', '#b8b8b8');
      this.g
        .append('line')
        .attr('y1', this.calculateRiskPoints(risk, 'right'))
        .attr('y2', this.calculateRiskPoints(risk, 'right'))
        .attr('x1', borderOffset)
        .attr('x2', this.width - borderOffset)
        .attr('stroke', '#b8b8b8');
    }

    // Y gridlines
    for (const area of [0.15, 0.4, 0.7, 1]) {
      this.g
        .append('line')
        .attr('x1', this.calculateAreaPoints(area, 'bottom'))
        .attr('x2', this.calculateAreaPoints(area, 'bottom'))
        .attr('y1', borderOffset)
        .attr('y2', this.width - borderOffset)
        .attr('stroke', '#b8b8b8');
      this.g
        .append('line')
        .attr('x1', this.calculateAreaPoints(area, 'top'))
        .attr('x2', this.calculateAreaPoints(area, 'top'))
        .attr('y1', borderOffset)
        .attr('y2', this.width - borderOffset)
        .attr('stroke', '#b8b8b8');
    }
  }

  private drawGridLinesSummer(): void {
    // add the X gridlines

    this.g
      .append('g')
      .attr('class', 'grid')
      .attr('transform', 'translate(0, ' + this.margin + ')')
      .call(
        this.make_x_gridlines()
          .tickSize(this.height - 2 * this.margin)
          .tickFormat('')
      );

    // Y gridlines
    this.g
      .append('g')
      .attr('class', 'grid')
      .attr('transform', 'translate(' + (this.width - this.margin) + ', 0)')
      .attr('stroke', '#b8b8b8')
      .call(
        this.make_y_gridlines()
          .tickSize(-this.width + 2 * this.margin, 0, 0)
          .tickFormat('')
      );
  }

  private make_x_gridlines(): d3Axis {
    return d3Axis.axisBottom(this.x).ticks(8);
  }
  private make_y_gridlines(): d3Axis {
    return d3Axis.axisRight(this.y).ticks(8);
  }

  private initAxis(): void {
    this.x = d3Scale.scaleLinear().rangeRound([0, this.width]);
    this.y = d3Scale.scaleLinear().rangeRound([0, this.height]);
    this.x.domain([-4.5, 4.5]);
    this.y.domain([4.5, -4.5]);
  }

  private drawAxis(): void {
    this.g
      .append('g')
      .attr('class', 'axis axis--x')
      .call(d3Axis.axisBottom(this.x))
      .attr('opacity', 0)
      .attr('transform', 'translate(0, ' + this.height / 2 + ')')
      .append('text');

    this.g
      .append('g')
      .attr('class', 'axis axis--y')
      .call(d3Axis.axisLeft(this.y).ticks(8))
      .attr('opacity', 0)
      .attr('transform', 'translate(' + this.width / 2 + ', 0)');
    //     .append('text')
  }

  private listenToSeasonChanges() {
    effect(
      () => {
        this.season();

        untracked(() => {
          this.initTranslations();
        });
      },
      { injector: this.injector }
    );
  }

  private listenToRiskBoxDataChanges() {
    effect(
      () => {
        this.riskBoxdata();

        untracked(() => {
          this.initTranslations();
          this.getTranslations().then(() => {
            this.createRiskBox();
          });
        });
      },
      { injector: this.injector }
    );
  }
}
